import React from "react";
import ReactDOM from "react-dom";
import { Theme } from "@radix-ui/themes";
import { AlertCircle, CheckCircle, Info } from "lucide-react";
import ModalTitleBar from "./ModalTitleBar";
import { TOUR_ANCHORS } from "./tourAnchors";

/**
 * The house alert/confirm dialog (recipe: docs/MODAL_STYLE.md) — tinted header
 * band with an identity mark, plain body, footer action bar. Buttons follow
 * the button hierarchy: callers list the affirmative action first, so
 * buttons[0] renders as the single primary (rightmost); the rest are
 * secondary. Error/warning titles switch the band and mark to danger.
 */
const MessageBox = ({
    title,
    message,
    buttons = ["OK"],
    onClick
}) => {
    const handleClick = (button) => {
        if (onClick) onClick(button);
    };

    const lowerTitle = title?.toLowerCase() || "";
    const isDanger = lowerTitle.includes("error") || lowerTitle.includes("warning");
    const isSuccess = lowerTitle.includes("success");
    const Icon = isDanger ? AlertCircle : isSuccess ? CheckCircle : Info;
    // The band itself now carries the semantics (is-danger / is-success), so the
    // icon no longer needs a filled badge to say "this one is bad".
    const bandVariant = isDanger ? "danger" : isSuccess ? "success" : "accent";

    //Primary sits rightmost; secondaries keep their relative order before it.
    const [primary, ...secondaries] = buttons;

    return ReactDOM.createPortal(
        <Theme>
            <div className="pointer-events-auto fixed inset-0 z-[9999] flex items-center justify-center" id={TOUR_ANCHORS.messageBox}>
                {/* Backdrop */}
                <div className="absolute inset-0" style={{ background: "rgba(15, 23, 42, 0.55)" }} />

                {/* Dialog */}
                <div
                    className="relative w-full max-w-md mx-4 overflow-hidden"
                    style={{
                        background: "var(--theme-surface)",
                        borderRadius: "var(--theme-radius-lg)",
                        boxShadow: "var(--theme-shadow-dialog)"
                    }}
                    role="alertdialog"
                    aria-modal="true"
                >
                    {title && <ModalTitleBar icon={Icon} title={title} variant={bandVariant} />}

                    <div className="modal-body-padded">
                        <p style={{ margin: 0, color: "var(--theme-fg)", fontSize: "0.9375rem", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                            {message == null ? '' : typeof message === 'string' ? message : String(message)}
                        </p>
                    </div>

                    <div className="modal-shell-footer">
                        {secondaries.map((button) => (
                            <button
                                key={button}
                                type="button"
                                className="btn-t btn-t-secondary"
                                onClick={() => handleClick(button)}
                            >
                                {button}
                            </button>
                        ))}
                        {primary && (
                            <button
                                key={primary}
                                type="button"
                                className={`btn-t ${isDanger && secondaries.length ? "btn-t-danger" : "btn-t-primary"}`}
                                onClick={() => handleClick(primary)}
                            >
                                {primary}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </Theme>,
        document.body
    );
};

export default MessageBox;
